import {type FC, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Button} from "@chakra-ui/react";
import iso6391 from "iso-639-1";
import uuid4 from "uuid4";
import CreateProjectStepSection from "../CreateProjectModal/CreateProjectStepSection.tsx";
import StepTargetLanguages from "../CreateProjectModal/StepTargetLanguages.tsx";
import {CreditEstimateLine} from "../Credits/CreditEstimateLine.tsx";
import type {IProject} from "../../models/interfaces/project.interface.ts";
import type {ILanguageConfiguration} from "../../models/interfaces/languageConfiguration.interface.ts";
import type {ICreditQuote} from "../../models/interfaces/credits.interface.ts";
import {useProjectsStore} from "../../store/useProjectsStore.ts";
import {useCreditsStore} from "../../store/useCreditsStore.ts";
import useSystemStore from "../../store/useSystemStore.ts";
import useUiStore from "../../store/useUiStore.ts";
import useUserStore from "../../store/useUserStore.ts";
import {buildCreditBreakdown, type ICreditBreakdown} from "../../utils/creditsBreakdown.ts";
import {DEFAULT_CREDIT_RATES} from "../../utils/creditsCost.ts";
import {isInsufficientCreditsError} from "../../utils/creditsErrors.ts";
import {billableSeconds} from "../../utils/projectCost.ts";
import {getLanguageDefaultConfig} from "../../utils/utils.ts";
import {modalPrimaryButtonProps, modalSecondaryButtonProps} from "../../theme/modalActionButtons.ts";

/**
 * The operation lease expired server-side. The backend refuses to replay this id at all, so a
 * retry MUST carry a fresh one — this is the only failure code that invalidates the current id.
 */
const OPERATION_EXPIRED_CODE = 1009;

/** Backend error codes from the add-languages contract, in the user's words. */
const ERROR_COPY_BY_CODE: Record<number, string> = {
  1003: "One of those languages is not supported for this project.",
  1005: "One of those languages is already being handled for this project.",
  1006: "This project's transcription has not finished yet.",
  1007: "This project cannot take new languages.",
  1008: "The selection changed since the last attempt — try adding them again.",
  [OPERATION_EXPIRED_CODE]: "This request expired before it completed — try adding them again.",
};

const GENERIC_ERROR_COPY = "We could not add those languages. Please try again.";

interface ApiErrorShape {
  response?: { data?: { code?: number } };
}

/** `addLanguagesToProject` rethrows the raw axios error; the contract code lives on the body. */
const responseCode = (error: unknown): number | undefined =>
  (error as ApiErrorShape | null)?.response?.data?.code;

/**
 * `sourceLanguage` and the `subtitleTranslations` keys are ISO 639-1 CODES by contract, but
 * `iso6391.getCode` answers "" for a value that is already a code, so this collapses either
 * form to a code without branching. A code/name mismatch here would silently offer back a
 * language the project already has and the API would reject.
 */
const toLanguageCode = (value: string): string => iso6391.getCode(value) || value;

export interface AddLanguagesModalProps {
  project: IProject;
}

const AddLanguagesModal: FC<AddLanguagesModalProps> = ({project}: AddLanguagesModalProps) => {
  const {closeModal, showToast} = useUiStore();
  const {supportedCodeLanguages} = useSystemStore();
  const {languageConfig, user} = useUserStore();
  const balance = useCreditsStore((state) => state.balance);
  const catalog = useCreditsStore((state) => state.catalog);
  const fetchBalance = useCreditsStore((state) => state.fetchBalance);
  const openNotEnough = useCreditsStore((state) => state.openNotEnough);
  const addLanguagesToProject = useProjectsStore((state) => state.addLanguagesToProject);
  // A successful add REPLACES the project in the store rather than merging into it, so read the
  // live copy: the prop is the snapshot the table row held when the dialog was opened, and the
  // background poll can move past it while this dialog is still on screen.
  const storedProject: IProject | undefined = useProjectsStore((state) =>
    state.projects.find((candidate: IProject) => candidate.id === project.id));
  const currentProject: IProject = storedProject ?? project;

  const [selectedTargets, setSelectedTargets] = useState<Record<string, ILanguageConfiguration>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const isSubmittingRef = useRef<boolean>(false);
  // ONE id for the life of the dialog. An ordinary retry replays it on purpose: that is what
  // makes a half-applied operation converge instead of adding the same languages twice.
  const [operationId, setOperationId] = useState<string>(() => uuid4());
  // Set only by a failure that leaves the CURRENT id bound, server-side, to the payload just
  // sent. Editing under such an id would come back as 1008, so the next edit mints a new one.
  const needsFreshOperationId = useRef<boolean>(false);

  const workspaceId: string | undefined = user?.workspaceId;

  useEffect(() => {
    if (workspaceId) void fetchBalance(workspaceId);
  }, [fetchBalance, workspaceId]);

  const sourceLanguageCode: string = toLanguageCode(currentProject.sourceLanguage);
  const sourceLanguageName: string = iso6391.getName(sourceLanguageCode) || currentProject.sourceLanguage;

  const configuredTargetCodes: string[] = useMemo(
    () => Object.keys(currentProject.featuresStates.subtitleTranslations || {}),
    [currentProject.featuresStates.subtitleTranslations]
  );

  const availableLanguageNames: string[] = useMemo(() => {
    const excludedCodes: Set<string> = new Set(
      [sourceLanguageCode, ...configuredTargetCodes].map(toLanguageCode)
    );
    return supportedCodeLanguages
      .filter((code: string) => !excludedCodes.has(code))
      .map((code: string) => iso6391.getName(code))
      .filter((name: string) => name !== "");
  }, [supportedCodeLanguages, sourceLanguageCode, configuredTargetCodes]);

  const markEdited = useCallback((): void => {
    if (!needsFreshOperationId.current) return;
    needsFreshOperationId.current = false;
    setOperationId(uuid4());
  }, []);

  const handleAddLanguage = useCallback((lang: string): void => {
    markEdited();
    setSelectedTargets((current: Record<string, ILanguageConfiguration>) => ({
      ...current,
      [lang]: getLanguageDefaultConfig(languageConfig, lang),
    }));
  }, [languageConfig, markEdited]);

  const handleRemoveLanguage = useCallback((lang: string): void => {
    markEdited();
    setSelectedTargets((current: Record<string, ILanguageConfiguration>) => {
      const remaining: Record<string, ILanguageConfiguration> = {...current};
      delete remaining[lang];
      return remaining;
    });
  }, [markEdited]);

  const handleUpdateConfig = useCallback((lang: string, update: Partial<ILanguageConfiguration>): void => {
    markEdited();
    setSelectedTargets((current: Record<string, ILanguageConfiguration>) => ({
      ...current,
      [lang]: {...current[lang], ...update},
    }));
  }, [markEdited]);

  const selectedLanguageNames: string[] = Object.keys(selectedTargets);
  const newLanguageCount: number = selectedLanguageNames.length;
  const mediaSeconds: number = billableSeconds(currentProject.mediaMeta?.videoDuration);
  const existingTranslationCount: number = configuredTargetCodes.length;
  const rates = catalog?.rates ?? DEFAULT_CREDIT_RATES;
  // Rows are attributed in ISO-code order, whatever order the languages were picked in, so the one
  // first-language row lands on the same language for everyone who selects the same set.
  const orderedNewCodes: string[] = useMemo(
    () => Object.keys(selectedTargets).map(toLanguageCode).sort(),
    [selectedTargets],
  );
  const breakdown: ICreditBreakdown = useMemo(
    () => buildCreditBreakdown({
      kind: "add",
      seconds: mediaSeconds,
      orderedTargetLanguages: orderedNewCodes,
      existingTranslationCount,
      dubbingScript: currentProject.dubbingScript,
      rates,
    }),
    [currentProject.dubbingScript, existingTranslationCount, mediaSeconds, orderedNewCodes, rates],
  );
  const quote: ICreditQuote = breakdown.quote;
  const availableCredits: number = balance?.balance ?? 0;
  const isBudgetKnown: boolean = balance !== null;
  const isOverBudget: boolean =
    isBudgetKnown && !balance?.unlimited && quote.total > availableCredits;
  const canSubmit: boolean = newLanguageCount > 0 && !isSubmitting;

  const ctaLabel: string = newLanguageCount === 0
    ? "Add languages"
    : `Add ${newLanguageCount} language${newLanguageCount === 1 ? "" : "s"}`;

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit || isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    // The API is keyed by ISO code; the picker works in display names, same as the create flow.
    const subtitles: Record<string, ILanguageConfiguration> = Object.fromEntries(
      Object.entries(selectedTargets).map(([name, config]: [string, ILanguageConfiguration]) =>
        [iso6391.getCode(name), config])
    );

    try {
      const fresh = (workspaceId ? await fetchBalance(workspaceId) : null) ?? balance;
      if (!fresh?.unlimited && quote.total > (fresh?.balance ?? 0)) {
        openNotEnough({requiredCredits: quote.total, availableCredits: fresh?.balance ?? 0});
        return;
      }
      await addLanguagesToProject(currentProject.id, subtitles, operationId);
      if (workspaceId) void fetchBalance(workspaceId);
      showToast(
        `${newLanguageCount} language${newLanguageCount === 1 ? "" : "s"} added to ${currentProject.name}`,
        "success"
      );
      await closeModal();
    } catch (error) {
      if (isInsufficientCreditsError(error) && error.response?.data) {
        needsFreshOperationId.current = false;
        setOperationId(uuid4());
        openNotEnough({
          requiredCredits: error.response.data.requiredCredits,
          availableCredits: error.response.data.availableCredits,
        });
        return;
      }
      const code: number | undefined = responseCode(error);
      if (code === OPERATION_EXPIRED_CODE) {
        // The id is dead server-side; mint a replacement now so the next attempt is not a
        // guaranteed second 1009. It has never been sent, so a later edit needs no further one.
        needsFreshOperationId.current = false;
        setOperationId(uuid4());
      } else {
        // Every other failure keeps the id: replaying it is what makes the retry idempotent.
        needsFreshOperationId.current = true;
      }
      showToast(code !== undefined ? ERROR_COPY_BY_CODE[code] ?? GENERIC_ERROR_COPY : GENERIC_ERROR_COPY, "error");
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    // Header and footer are shrink-0 and only the middle scrolls, so the CTA stays reachable no
    // matter how many languages are picked. Without this the dialog grew past the viewport and,
    // with the overlay unable to scroll, became unsubmittable at about six languages.
    <div className="flex min-h-0 w-full flex-1 flex-col p-8 sm:p-6">
      <header className="flex shrink-0 flex-col">
        <h2 className="text-2xl font-semibold text-white text-balance">Add languages</h2>
        <p className="mt-1 text-sm text-n-300 text-pretty">
          New languages use this project&apos;s existing {sourceLanguageName} transcription.
          Languages already on {currentProject.name} are left untouched.
        </p>
      </header>

      <div className="mt-6 flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto">
        <CreateProjectStepSection
          testId="add-languages-targets-section"
          title="New target languages"
          description="Each added language is translated from the source and billed separately."
          action={newLanguageCount > 0 ? (
            <p className="shrink-0 text-sm text-n-300 tabular-nums">{newLanguageCount} selected</p>
          ) : undefined}
        >
          <StepTargetLanguages
            availableLanguages={availableLanguageNames}
            selectedSourceLanguage={sourceLanguageName}
            selectedTargets={selectedTargets}
            onAddLanguage={handleAddLanguage}
            onRemoveLanguage={handleRemoveLanguage}
            setTargetConfig={handleUpdateConfig}
          />
        </CreateProjectStepSection>

        <CreateProjectStepSection
          testId="add-languages-cost-box"
          data-over-budget={String(isOverBudget)}
          tone={isOverBudget ? "danger" : "accent"}
          title="Credits"
          description={
            !isBudgetKnown
              ? "Checking how many credits this workspace has left…"
              : newLanguageCount === 0
                ? "Pick a language to see an estimate."
                : isOverBudget
                  ? `This project needs ${quote.total} credits but you have ${availableCredits} left.`
                  : "You have enough credits to add these languages."
          }
          action={
            // An empty selection has no rows worth explaining, and a project whose media duration never
            // loaded quotes 0 minutes (Rocky refuses that add with 409 1014): both keep the bare figure.
            newLanguageCount > 0 && quote.billedMinutes > 0 ? (
              // The panel opens upward: this card is the last thing in the scroll area, so a downward
              // panel would only add scroll height below the footer instead of showing.
              <CreditEstimateLine
                breakdown={breakdown}
                isOverBudget={isOverBudget}
                placement="top"
                size="lg"
                testId="add-languages-estimate"
              />
            ) : (
              <p
                className={`shrink-0 text-2xl font-semibold leading-8 tabular-nums ${
                  isOverBudget ? "text-red-500" : "text-brandPurple-200"
                }`}
              >
                {quote.total} credits
              </p>
            )
          }
        />
      </div>

      <div className="mt-6 flex shrink-0 justify-end gap-3 border-t border-n-500 pt-6">
        <Button {...modalSecondaryButtonProps} onClick={() => closeModal()}>
          Cancel
        </Button>
        <Button
          {...modalPrimaryButtonProps}
          isDisabled={!canSubmit}
          isLoading={isSubmitting}
          loadingText={ctaLabel}
          onClick={handleSubmit}
        >
          {ctaLabel}
        </Button>
      </div>
    </div>
  );
};

export default AddLanguagesModal;
